import { Analytics, API, Auth } from "aws-amplify";
import {
  AUTH_USER,
  AUTH_MFA,
  AUTH_NEW_PASSWORD_REQUIRED,
  UNAUTH_USER,
  REGISTER_USER,
  REGISTER_USER_CONFIRM,
  REGISTER_MFA,
  REGISTER_USER_ERROR,
  FORGOT_PASSWORD,
  FORGOT_PASSWORD_CONFIRM,
  AUTH_ERROR,
  IS_LOADING,
  LOGIN_REGISTER_CONFIRMATION,
  PUSH_NOTIFICATION,
  REFRESHING_COGNITO_USER_TOKENS
} from "./types";
import { ANALYTICS_EVENT } from "../../common/ApmlifyRecordEvents";

import _ from "lodash";
import { fetchSkill } from "./skillActions";
import { generateUserPicture } from "../../common/utils";
import { AWS, Credentials } from "@aws-amplify/core";
import {getEnvironmentName} from "../../utils"
const environment = getEnvironmentName()
const uuidv4 = require("uuid/v4");
const config = require("app_configuration").CONFIGURATION[environment];
/*
 * ENVS_WITHOUT_ALT_MIGRATION_FLOW are the environments that don't need a login flow for migrating users. We'll never
 * need to migrate users from development/signup into signup/development. Both of these envs don't have (or need) the
 * alt login flow enabled in respective Cognito pools and they also lack Lambdas used for user migration between
 * environments.
 */
const ENVS_WITHOUT_ALT_MIGRATION_FLOW = ["development", "signup"];

const API_REQUEST_FIXED_PARAMS = {
  Version: config.RESPONSUM_API_VERSION,
  Api: config.RESPONSUM_API,
  AccountType: config.RESPONSUM_API_RESOURCE_TYPE.PRIVATE
};

export function authError(error) {
  return {
    type: AUTH_ERROR,
    payload: error.message
  };
}

/**
 * This function is meant to be called on a user's first login.
 * It will create a new Dynamo user, new skill (and bot) if needed and put the new skill info into the applications'
 * state.
 * @param {object} data - data returned after successful Auth.signIn.
 * @param {function} dispatch - Used to dispatch the fetchSkill action.
 */
const firstLoginSetup = (data, dispatch) => {
  const noNeedForNewSkillMsg = "No need to create skill";

  // Create new Dynamo user
  API.post(config.AWS_API_NAME, "users/new", {
    body: {
      AccessToken: data.signInUserSession.accessToken.jwtToken,
      ApiCall: "CreateDbUser",
      FormParams: `email=${encodeURIComponent(
        _.get(data, "attributes.email", _.get(data, "challengeParam.userAttributes.email", ""))
      )}&authtype=NULL`,
      DeleteFirstLoginAttr: true,
      ...API_REQUEST_FIXED_PARAMS
    }
  })
    .then(newDbUserResponse => {
      // Create new skill for users which registered by themselves, don't create a skill for users which registered by
      // an invite link (those users already belong to an existent tenant with an existing skill)
      if (newDbUserResponse.code === 200 && newDbUserResponse.shouldCreateSkill) {
        const skillName = _.get(data, "attributes.email", _.get(data, "challengeParam.userAttributes.email", ""))
          .toLowerCase()
          .replace(/[^a-zA-Z]/g, " ");

        // Create first skill with default intents for new user
        return API.post(config.AWS_API_NAME, "skill/build-bot", {
          body: {
            AccessToken: data.signInUserSession.accessToken.jwtToken,
            ApiCall: "CreateSkill",
            FormParams: `appId=${uuidv4()}&name=${encodeURIComponent(skillName)}&invocationName=${encodeURIComponent(
              skillName
            )}&description=&createAlexaSkillToggle=false`,
            ...API_REQUEST_FIXED_PARAMS
          }
        });
      } else if (newDbUserResponse.code === 200) {
        throw new Error(noNeedForNewSkillMsg);
      } else {
        throw new Error(newDbUserResponse.message);
      }
    })
    .then(newSkillResponse => {
      if (newSkillResponse.code !== 200) {
        throw new Error(newSkillResponse.message);
      } else {
        dispatch(fetchSkill()); // reload user skill info since we now have a fresh skill
      }
    })
    .catch(err => {
      // First handle expected errors -- this is the case when we just create a DB user without skill,
      // an error was thrown on purpose in order to break the .then() chain
      if (err.message === noNeedForNewSkillMsg) {
        return;
      }

      // todo show some error to user?
      console.log("ERROR |", err);
    });
};

// Cognito - Auth.signIn()
export function login(username, password, history, location = null, loginWithUserPasswordAuthFlow = false) {
  return function(dispatch) {
    if (loginWithUserPasswordAuthFlow) {
      // This alternative login flow is used when we need to migrate users in a new tenant user pool
      // More info:
      // https://docs.aws.amazon.com/cognito/latest/developerguide/cognito-user-pools-import-using-lambda.html
      Auth.configure({
        authenticationFlowType: "USER_PASSWORD_AUTH"
      });
    } else {
      // This is the default login flow
      Auth.configure({
        authenticationFlowType: "USER_SRP_AUTH"
      });
    }

    // console.log("actions.login(): username password:", { username, password });

    // signIn (cognito)
    Auth.signIn(username, password)
      .then(data => {
        // success -- dispatch AUTH_USER
        // console.log("INFO | actions.login():Auth.signIn() data:", data);
        // console.log(Auth.currentCredentials());

        // inspect response 'data' and check whether
        // 1. MFA confirmation is required, dispatch->AUTH_MFA
        // 2. New Password is required (change 'FORCE_CHANGE_PASSWORD'
        //    to 'CONFIRMED'), dispatch-> AUTH_NEW_PASSWORD_REQUIRED with payload
        // 3. otherwise, authenticate user, dispatch -> AUTH_USER
        if (data.challengeName === "NEW_PASSWORD_REQUIRED" || data.challengeName === "FORCE_CHANGE_PASSWORD") {
          dispatch({ type: AUTH_NEW_PASSWORD_REQUIRED, payload: data });
        } else if (
          data.challengeName === "MFA_REQUIRED" ||
          data.challengeName === "SMS_MFA" ||
          data.challengeName === "SOFTWARE_TOKEN_MFA"
        ) {
          dispatch({ type: AUTH_MFA, payload: data });
        } else {
          // dispatch AUTH_USER
          Auth.currentAuthenticatedUser({ bypassCache: true }).then(currentAuthUser => {
            Analytics.updateEndpoint({
              attributes: {
                tenant: [currentAuthUser.attributes["custom:tenant"]]
              },
              channelType: "EMAIL",
              address: currentAuthUser.attributes.email,
              userId: currentAuthUser.username,
              userAttributes: _.mapValues(currentAuthUser.attributes, value => [`${value}`])
            }).then(() => {
              Analytics.record(ANALYTICS_EVENT().LOGIN.success);
            });
            dispatch({ type: AUTH_USER, payload: currentAuthUser });
            dispatch(fetchSkill());
            // we have authenticated, lets navigate to /main route
            const nextRoute = _.get(location, "state.from.pathname", "/dashboard");

            // Check if this is the first time the user is signing in
            if (data.attributes["custom:first_login"] === "1") {
              firstLoginSetup(data, dispatch);
              history.push("/getting-started-wizard");
              return;
            }

            history.push(nextRoute);
          });
        }
      })
      .catch(err => {
        console.error("actions.login():Auth.signIn() err:", err);
        Analytics.record(ANALYTICS_EVENT(err.message).LOGIN.error);
        if (err.code === "UserNotConfirmedException") {
          // console.log("actions.login():Auth.signIn() UserNotConfirmedException");
          Auth.resendSignUp(username)
            .then(data => {
              // console.log("actions.resendConfirmationCode():Auth.resendSignUp() data:", data);

              dispatch({ type: LOGIN_REGISTER_CONFIRMATION });
            })
            .catch(err => {
              dispatch(authError(err));
            });
          // dispatch({ type: LOGIN_REGISTER_CONFIRMATION });
        } else if (err.code === "PasswordResetRequiredException") {
          // Auth.forgotPassword(username).then(data => {
          //   console.log("actions.forgotPassword():Auth.forgotPassword() data:", data);
          //
          //   dispatch({ type: FORGOT_PASSWORD });
          // });
          history.push("/forgot-password");
        } else if (
          err.code === "UserNotFoundException" &&
          err.message === "User does not exist." &&
          !loginWithUserPasswordAuthFlow &&
          !ENVS_WITHOUT_ALT_MIGRATION_FLOW.includes(config.RESPONSUM_API)
        ) {
          // Try login flow for migrating users to a new user pool (ie new tenant)
          // NB: This will be triggered for any bad username/password combination
          return login(username, password, history, location, true)(dispatch);
        } else if (
          err.code === "UserNotFoundException" &&
          err.message.startsWith("Exception migrating user in app client")
        ) {
          // Instead of showing "Exception migrating user in app client XXXXXX" to user when user migration fails
          // (eg for wrong username/password), just show the standard message in order to not confuse the user
          err.message = "User does not exist.";
        }

        // error -- invoke authError which dispatches AUTH_ERROR
        dispatch(authError(err));
      });
  };
}

// Cognito - Auth.currentAuthenticatedUser()
// Cognito - Auth.userSession()
// This is a pass-through function to indicate that user has already authenticated
// and has a valid Amplify session.
export function validateUserSession() {
  return function(dispatch) {
    // console.log("actions.validateUserSession()");

    Auth.currentAuthenticatedUser({ bypassCache: true })
      .then(currentAuthUser => {
        // console.log("actions.validateUserSession():Auth.currentAuthenticatedUser() currentAuthUser:", currentAuthUser);
        // grab the user session
        Auth.userSession(currentAuthUser)
          .then(session => {
            // console.log("actions.validateUserSession():Auth.userSession() session:", session);
            // finally invoke isValid() method on session to check if auth tokens are valid
            // if tokens have expired, lets call "logout"
            // otherwise, dispatch AUTH_USER success action and by-pass login screen
            if (session.isValid()) {
              // fire user is authenticated
              Analytics.updateEndpoint({
                attributes: {
                  tenant: [currentAuthUser.attributes["custom:tenant"]]
                },
                channelType: "EMAIL",
                address: currentAuthUser.attributes.email,
                userId: currentAuthUser.username,
                userAttributes: _.mapValues(currentAuthUser.attributes, value => [`${value}`])
              }).then(() => {});
              dispatch({ type: AUTH_USER, payload: currentAuthUser });
              dispatch(fetchSkill());
              // logout();
              //history.push('/');
            } else {
              // fire user is unauthenticated
              dispatch({ type: UNAUTH_USER });
              //history.push('/');
            }
          })
          .catch(err => {
            console.error("actions.validateUserSession():Auth.userSession() err:", err);
            // error occured during session validation, fire user is unauthenticated
            dispatch({ type: UNAUTH_USER });
            //history.push('/');
          });
      })
      .catch(err => {
        console.error("actions.validateUserSession():Auth.currentAuthenticatedUser() err:", err);
        // error occured while retrieving current auth user, fire user is unauthenticated
        dispatch({ type: UNAUTH_USER });
        //history.push('/');
      });
  };
}

// Cognito - Auth.completeNewPassword()
export function setNewPassword(cognitoUser, newPassword, history) {
  return function(dispatch) {
    // console.log("actions.setNewPassword(): cognitoUSer, newPassword:", {
    //   cognitoUser,
    //   newPassword
    // });

    // completeNewPassword (cognito)
    Auth.completeNewPassword(cognitoUser, newPassword)
      .then(data => {
        // console.log("actions.setNewPassword():Auth.completeNewPassword() data: ", data);

        // inspect response 'data' and check whether
        // 1. MFA confirmation is required, dispatch->AUTH_MFA
        // 2. otherwise, authenticate user, dispatch -> AUTH_USER
        if (data.challengeName === "SMS_MFA") {
          dispatch({ type: AUTH_MFA, payload: data });
        } else {
          // dispatch AUTH_USER
          dispatch({ type: AUTH_USER, payload: data });

          // we have authenticated, lets navigate to /main route
          if (data.challengeParam.userAttributes["custom:first_login"] === "1") {
            firstLoginSetup(data, dispatch);
            history.push("/getting-started-wizard");
            return;
          }
          history.push("/dashboard");
        }
      })
      .catch(err => {
        console.error("actions.setNewPassword():Auth.completeNewPassword() err:", err);
        // error -- invoke authError which dispatches AUTH_ERROR
        dispatch(authError(err));
      });
  };
}

export function setChangePassword(oldPassword, newPassword, history) {
  return function(dispatch) {
    Auth.currentAuthenticatedUser()
      .then(user => {
        return Auth.changePassword(user, oldPassword, newPassword);
      })
      .then(data => {
        // console.log(data);
        dispatch({
          type: PUSH_NOTIFICATION,
          payload: { message: "Password changed successfully.", type: "success" }
        });
      })
      .catch(err => {
        console.log(err);
        let message;
        if (err.code === "NotAuthorizedException") {
          message = "Current password provided is not correct";
        } else {
          message = err.message;
        }
        dispatch({
          type: PUSH_NOTIFICATION,
          payload: { message: message, type: "error" }
        });
      });
  };
}

// Cognito - Auth.signOut()
export function logout({ username }, history) {
  return function(dispatch) {
    // console.log("actions.logout(): username: ", username);

    // signOut (cognito)
    Auth.signOut()
      .then(data => {
        // console.log("actions.logout():Auth.signOut() data:", data);
        dispatch({ type: UNAUTH_USER });

        // we have authenticated, lets navigate to /main route
        history.push("/sign-in");
      })
      .catch(err => {
        console.error("actions.logout():Auth.signOut() err:", err);
        // error -- invoke authError which dispatches AUTH_ERROR
        dispatch(authError(err));
      });
  };
}

// Cognito - Auth.confirmSignIn()
export function confirmLogin({ cognitoUser, code }, history, location) {
  return function(dispatch) {
    // console.log("actions.confirmLogin(): cognitoUSer, code:", {
    //   cognitoUser,
    //   code
    // });

    // confirmSignIn (cognito)
    Auth.confirmSignIn(cognitoUser, code, cognitoUser.challengeName)
      .then(data => {
        // console.log("actions.confirmLogin():Auth.confirmSignIn() data: ", data);

        Auth.currentAuthenticatedUser({ bypassCache: true }).then(currentAuthUser => {
          dispatch({ type: AUTH_USER, payload: currentAuthUser });
          // we have authenticated, lets navigate to /main route
          const nextRoute = _.get(location, "state.from.pathname", "/dashboard");
          history.push(nextRoute);
        });

        // we have authenticated, lets navigate to /main route
        // history.push("/dashboard");
      })
      .catch(err => {
        console.error("actions.confirmLogin():Auth.confirmSignIn() err:", err);
        // error -- invoke authError which dispatches AUTH_ERROR
        dispatch(authError(err));
      });
  };
}

// Cognito - Auth.signUp()
export function register(email, password, history) {
  return dispatch => {
    // console.log("actions.register(): username, password: ", email, password);

    // const username = email.split("@")[0];
    // console.log("USERNAME", username);

    generateUserPicture(null, email).then(image => {
      // signUp (cognito)
      Auth.signUp({
        username: email,
        password: password,
        attributes: {
          email: email,
          "custom:tenant": API_REQUEST_FIXED_PARAMS.Api === "development" ? "dev-" + email : email,
          "custom:first_login": "1",
          "custom:create_skill": "1",
          "custom:role": "Administrator",
          picture: image
        }
      })
        .then(data => {
          // console.log("actions.register():Auth.signUp() data:", data);

          // MFA is required for user registration
          if (typeof data.userConfirmed != "undefined" && data.userConfirmed === false) {
            dispatch({ type: REGISTER_MFA, payload: data });
            dispatch({ type: IS_LOADING, payload: false });
          } else {
            dispatch({ type: REGISTER_USER });

            // user registration successful, lets navigate to / route
            history.push("/");
          }
        })
        .catch(err => {
          console.error("actions.register():Auth.signUp() err:", err);

          // error -- invoke authError which dispatches REGISTER_USER_ERROR
          dispatch(authError(err));
        });
    });
  };
}

// Cognito - Auth.confirmSignUp()
export function confirmRegistration({ cognitoUser, code, email, password }, history) {
  return function(dispatch) {
    // console.log("actions.confirmRegistration(): cognitoUSer, code:", {
    //   cognitoUser,
    //   code
    // });
    const username = _.get(cognitoUser, "user.username", _.get(cognitoUser, "user"));

    // console.log("CognitoUser : ", cognitoUser.user);

    // confirmSignUp (cognito)
    Auth.confirmSignUp(username, code)
      .then(data => {
        // console.log("actions.confirmRegistration():Auth.confirmSignUp() data: ", data);

        // A successful registration response doesnt contain idToken.
        // So we must redirect to login screen

        // dispatch REGISTER_USER_CONFIRM
        // dispatch({ type: REGISTER_USER_CONFIRM });

        setTimeout(() => {
          dispatch(login(email, password, history));
        }, 2000);

        // we have authenticated, lets navigate to /main route
        // history.push("/sign-in");
      })
      .catch(err => {
        console.error("actions.confirmRegistration():Auth.confirmSignUp() err:", err);

        // error -- invoke authError which dispatches AUTH_ERROR
        //dispatch(authError(err));
        dispatch({
          type: REGISTER_USER_ERROR,
          payload: err.message,
          cognitoUser
        });
      });
  };
}

// Cognito - Auth.resendSignUp()
export function resendConfirmationCode(cognitoUser, history) {
  console.log("resendConfirmationCode- enter", cognitoUser);
  return function(dispatch) {
    console.log("actions.resendConfirmationCode(): username: ", cognitoUser);
    // const { username } = cognitoUser;

    console.log(_.get(cognitoUser, "username", _.get(cognitoUser, "cognitoUser.user.username")));

    // resendSignUp (cognito)
    Auth.resendSignUp(_.get(cognitoUser, "username", _.get(cognitoUser, "cognitoUser.user.username")))
      .then(data => {
        console.log("actions.resendConfirmationCode():Auth.resendSignUp() data:", data);

        dispatch({
          type: PUSH_NOTIFICATION,
          payload: { message: "Please check your email. Confirmation code sent successfully.", type: "success" }
        });

        // dispatch({ type: REGISTER_MFA, payload: cognitoUser });
      })
      .catch(err => {
        console.error("actions.resendConfirmationCode():Auth.resendSignUp() err:", err);

        // error -- invoke authError which dispatches AUTH_ERROR
        dispatch(authError(err));
      });
  };
}

// Cognito - Auth.forgotPassword()
export function forgotPassword(username, history) {
  return function(dispatch) {
    // console.log("actions.forgotPassword(): username: ", username);

    // forgotPassword (cognito)
    Auth.forgotPassword(username)
      .then(data => {
        // console.log("actions.forgotPassword():Auth.forgotPassword() data:", data);

        dispatch({ type: FORGOT_PASSWORD });
      })
      .catch(err => {
        console.error("actions.forgotPassword():Auth.forgotPassword() err:", err);

        // error -- invoke authError which dispatches AUTH_ERROR
        dispatch(authError(err));
      });
  };
}

// Cognito - Auth.forgotPasswordSubmit()
export function confirmForgotPassword(username, code, newPassword, history) {
  return function(dispatch) {
    // console.log("actions.confirmForgotPassword(): username, code, newPassword: ", username, code, newPassword);

    // forgotPasswordSubmit (cognito)
    Auth.forgotPasswordSubmit(username, code, newPassword)
      .then(data => {
        // console.log("actions.confirmForgotPassword():Auth.forgotPasswordSubmit() data:", data);

        // TODO - User password changed successfully, do we need to login again?
        dispatch({ type: FORGOT_PASSWORD_CONFIRM });

        history.push("/sign-in");
      })
      .catch(err => {
        console.error("actions.confirmForgotPassword():Auth.forgotPasswordSubmit() err:", err);

        // error -- invoke authError which dispatches AUTH_ERROR
        dispatch(authError(err));
      });
  };
}

const refreshSessionPromise = async refreshToken => {
  return new Promise(async (resolve, reject) => {
    const user = await Auth.currentAuthenticatedUser();
    return user.refreshSession(refreshToken, async (err, data) => {
      if (err) {
        reject(err);
      } else {
        resolve(data); // THIS IS YOUR REFRESHED ATTRIBUTES/GROUPS
      }
    });
  });
};

const refreshCurrentSession = async () => {
  const session = await Auth.currentSession();
  return refreshSessionPromise(session.getRefreshToken());
};

export const refreshingCongnitoUserTokens = () => async (dispatch, getState) => {
  const state = getState();

  const refreshedDate = _.get(state, "auth.cognitoTokensRefreshedTime", new Date(Date.now() - 10000 * 60)); // default value - 10 min

  if (new Date() - refreshedDate > 300000) {
    try {
      //a way to refresh jwt access token
      await refreshCurrentSession();

      // 300000 ms = 5 minutes
      let result = await Auth.currentSession();
      let credentials = await Credentials.set(result, "session");
      AWS.config.update({
        credentials
      });
      dispatch({
        type: REFRESHING_COGNITO_USER_TOKENS,
        payload: { tokens: result, updatedDate: new Date() }
      });
    } catch {}
  }
};

export function isLoading(state) {
  return function(dispatch) {
    dispatch({ type: IS_LOADING, payload: state });
  };
}
